import {
    Button, ChangeEvent, ClickEvent, Component, DataSourceAction, DataSourceExecutionEvent, DataSourceMode,
    DataSourceModeChangeEvent, Layout, SlideoutDecorator
} from "@mcleod/components";
import { AutogenLayoutSettlementPayrollHistory } from "./autogen/AutogenLayoutSettlementPayrollHistory";
import { Api, DisplayType, DisplayValue, HorizontalAlignment, LogManager, ModelRow, Navigation } from "@mcleod/core";
import { CommonDialogs, ImageTable } from "@mcleod/common";
import { SetStateLocalTaxCode } from "./SetStateLocalTaxCode";

const log = LogManager.getLogger("lme.settlement.SettlementPayrollHistory");

export class SettlementPayrollHistory extends AutogenLayoutSettlementPayrollHistory {

    onLoad(): void | Promise<void> {
        this.sourceDrsTimecardHist.addAfterExecutionListener((event: DataSourceExecutionEvent) => {
            this.sourceDrsTimecardHist.mode = DataSourceMode.UPDATE;
        });
        this.layoutTableLeave.tableLeave.removeColumn("columnHeaderID");
        this.layoutTimecardHistoryDetails.textboxDrsPayrollHistpayperiodStart.visible = false;
        this.layoutTimecardHistoryDetails.textboxDrsPayrollHistpayperiodEnd.visible = false;
        this.layout401kDetails.replaceMainDatasource(this.sourceDrs401kHist);
    }

    alignCheckInfoCaptionToRight() {
        const textboxes = [
            this.textboxFed,
            this.textboxSuiWagesTot,
            this.textboxTotGrossPay,
            this.textboxMedicare,
            this.textboxNTaxable,
            this.textboxSUIWage,
            this.textboxState,
            this.textboxFuta,
            this.textboxCounty,
            this.textboxSdi,
            this.textboxETT,
            this.textboxCity,
            this.textboxSSN,
            this.textboxPfml,
            this.textboxFederal,
            this.textboxStateWith,
            this.textboxMed,
            this.textboxCountyWith,
            this.textboxSuiWith,
            this.textboxsdi,
            this.textboxCityWith,
            this.textboxSSWith,
            this.textboxPfmlWith,
            this.textboxEmployerPfml,
            this.textboxEmployerEtt,
            this.textboxEmployerSdi,
            this.textboxEmployerFui,
            this.textboxEmployerSui,
            this.textboxEmployerFicaHi,
            this.textboxEmployerFicaSs
        ];
        textboxes.forEach(textbox => {
            textbox.captionLabel.align = HorizontalAlignment.RIGHT;
            textbox.captionLabel.marginRight = -8;
        });
    }

    doAfterExecute(event) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            this.addLayoutDataLoadListener(() => {
                (this.layoutImageTable as ImageTable).populateForRow("H", this.mainDataSource.activeRow.get("id"));
                (this.layoutImageTable as ImageTable).buttonUploadImage.enabled = false;
            });
            this.buttonReverse.visible = !this.activeRow.getBoolean("is_void", false);
        }
    }

    async buttonStateTaxCodeOnClick(event: ClickEvent) {
        const layout = Layout.getLayout("lme/settlement/SetStateLocalTaxCode") as SetStateLocalTaxCode;
        const saveButton = new Button({
            caption: "Save & Close",
            rowBreak: false,
            color: "primary.reverse",
            backgroundColor: "primary",
            borderWidth: 1,
            borderColor: "primary",
            onClick: async (event: ClickEvent) => {
                // at least one of the enabled tax code fields must be set
                if (layout.validateSimple()) {
                    if (!layout.panelTaxCodes.components.some((comp: Component) => {
                        return comp.enabled && !comp.isEmpty();
                    })) {
                        CommonDialogs.showError("At least one of the enabled tax code fields must be set.");
                        return;
                    }
                } else {
                    return;
                }
                saveButton.busy = true;
                try {
                    await Api.update("lme/settlement/set-state-local-tax-code", {
                        update_current_only: layout.activeRow.getBoolean("update_current_only", false),
                        current_record_id: this.activeRow.get("id"),
                        check_date_from: new Date(layout.excludabletextboxCheckDate.textboxRangeStart?.getDataValue() ?? layout.excludabletextboxCheckDate.textbox?.getDataValue()),
                        check_date_to: new Date(layout.excludabletextboxCheckDate.textboxRangeEnd?.getDataValue() ?? layout.excludabletextboxCheckDate.textbox?.getDataValue()),
                        payee_id: layout.textboxPayee.lookupModelData?.map((row: ModelRow) => row.get("id")).join(","),
                        state_tax_id: layout.activeRow.get("state_tax_id"),
                        county_tax_code: layout.activeRow.get("county_tax_code"),
                        city_tax_code: layout.activeRow.get("city_tax_code"),
                        sui_tax_code: layout.activeRow.get("sui_tax_code")
                    });
                    layout.slideOut();
                    this.mainDataSource.search(this.mainDataSource.lastSearch);
                } catch (error) {
                    log.error("Error saving state/local tax code:", error);
                }
                saveButton.busy = false;
            }
        });
        SlideoutDecorator.decorateAndSlideIn(layout, {
            title: "Set State/Local Tax Code",
            fillHeight: true,
            width: "30%",
            addlComponents: [saveButton],
            layoutLoadListeners: () => {
                const setCurrentRecordDefaults = () => {
                    if (!this.activeRow.isNull("payee_id")) {
                        layout.textboxPayee.displayComponentData(this.activeRow, null, 0);
                        layout.textboxPayee.searchOnly = true;
                    }
                    if (!this.activeRow.isNull("check_date")) {
                        layout.excludabletextboxCheckDate.textbox.text = DisplayValue.getDateDisplayValue(this.activeRow.getDate("check_date"), DisplayType.DATE);
                    }
                    if (!this.activeRow.isNull("state_tax_id")) {
                        layout.textboxState.displayComponentData(this.activeRow, null, 0);
                    }
                    if (!this.activeRow.isNull("county_tax_code")) {
                        layout.textboxCounty.displayComponentData(this.activeRow, null, 0);
                    }
                    if (!this.activeRow.isNull("city_tax_code")) {
                        layout.textboxCity.displayComponentData(this.activeRow, null, 0);
                    }
                    if (!this.activeRow.isNull("sui_tax_code")) {
                        layout.textboxSUI.displayComponentData(this.activeRow, null, 0);
                    }
                };
                layout.mainDataSource.addAfterModeChangeListener((event: DataSourceModeChangeEvent) => {
                    layout.checkboxUpdateCurrentRecordOnly.addChangeListener((event: ChangeEvent) => {
                        if (event.newValue === true) {
                            setCurrentRecordDefaults();
                        }
                        layout.textboxPayee.enabled = !layout.checkboxUpdateCurrentRecordOnly.checked;
                        layout.textboxState.enabled = !layout.checkboxUpdateCurrentRecordOnly.checked || this.activeRow.isNull("state_tax_id");
                        layout.textboxCounty.enabled = !layout.checkboxUpdateCurrentRecordOnly.checked || this.activeRow.isNull("county_tax_code");
                        layout.textboxCity.enabled = !layout.checkboxUpdateCurrentRecordOnly.checked || this.activeRow.isNull("city_tax_code");
                        layout.textboxSUI.enabled = !layout.checkboxUpdateCurrentRecordOnly.checked || this.activeRow.isNull("sui_tax_code");
                        layout.excludabletextboxCheckDate.required = layout.excludabletextboxCheckDate.enabled = !layout.checkboxUpdateCurrentRecordOnly.checked;
                    });
                    layout.checkboxUpdateCurrentRecordOnly.checked = true;
                });
                layout.mainDataSource.mode = DataSourceMode.ADD;
            }
        });
    }

    buttonReverseOnClick(event: ClickEvent) {
        try {
            const urlParams = new URLSearchParams();
            const checkNumber = this.activeRow?.get("check_number");
            if (checkNumber != null) {
                const padded = String(checkNumber).padStart(8, "0");
                urlParams.set("checkNumberFrom", padded);
                urlParams.set("checkNumberTo", padded);
            }
            const checkDate = this.activeRow?.getDate("check_date");
            if (checkDate != null) {
                const dateText = DisplayValue.getDateDisplayValue(checkDate, DisplayType.DATE);
                urlParams.set("checkDateFrom", dateText);
                urlParams.set("checkDateTo", dateText);
                urlParams.set("voidDate", dateText);
            }
            const payeeId = this.activeRow?.get("payee_id");
            if (payeeId != null) {
                urlParams.set("payeeIdSearch", String(payeeId));
            }
            const factoringCompanyId = this.activeRow?.get("factoring_company_id");
            if (factoringCompanyId != null) {
                urlParams.set("factoringCompanyIdSearch", String(factoringCompanyId));
            }
            const url = `lme/settlement/ReverseSettlementCheck?${urlParams.toString()}`;
            Navigation.navigateTo(url, { newTab: true });
        } catch (error) {
            log.error("Error opening Reverse Settlement Check", error);
        }
    }

    sourceDrsPayrollHistAfterModeChange(event: DataSourceModeChangeEvent) {
        if (event.newMode === DataSourceMode.SEARCH) {
            this.buttonStateTaxCode.visible = false;
            this.buttonReverse.visible = false;
        } else {
            this.alignCheckInfoCaptionToRight();
        }
        this.dataHeader.showExecute = event.newMode === DataSourceMode.SEARCH;
    }
}
